import React from "react";
import { motion } from "framer-motion";
import { useMarketing } from "@/components/marketing/MarketingLayout";
import { CaretDown, Check, ArrowRight } from "@phosphor-icons/react";
import {
  fadeUp, stagger, Reveal, Overline, EditorialHero,
} from "@/components/marketing/editorial";

const DOMAINS = [
  { id: "close", n: "01", c: "Close & Controllership", sub: "Month-end close, reconciliations, accruals, variance analysis",
    problem: "Close slips every month because checklists, recs, accruals and variance commentary are manual and scattered across spreadsheets and chat.",
    how: "Agent orchestrates your checklist, runs recs overnight, calculates accruals from history, drafts flux commentary and predicts close date daily.",
    output: "Live close dashboard, completed recs with exceptions, staged accrual JEs, variance memos and an audit trail on every task.",
    workflows: ["Close orchestration","Task management & checklists","Journal entry creation & validation","Accruals & provisions","Balance sheet reconciliation","Bank reconciliation","Intercompany reconciliation","Variance / flux analysis"],
    stats: [["3 days","Median close, down from 7–10"],["−40%","Controller time chasing status"],["95%","Bank txns auto-matched"],["100%","Tasks with audit evidence"]] },
  { id: "p2p", n: "02", c: "Procure to Pay", sub: "Invoice capture, matching, validation, payment controls",
    problem: "At scale, AP spends most time on the 15% of invoices that don't match. Duplicates slip through and GL coding is inconsistent.",
    how: "OCR extracts invoice data. Agent matches PO + GR, checks tolerances, auto-posts clean matches, blocks duplicates and fraud-screens payments.",
    output: "Auto-posted invoices with audit trail, an exception queue with AI resolutions, blocked duplicates and validated payment proposals.",
    workflows: ["Invoice capture & extraction","Invoice validation (PO / non-PO)","3-way matching","Duplicate detection","Vendor master validation","GL coding & classification","Payment proposal validation","Vendor spend analytics"],
    stats: [["78%","Invoices auto-posted"],["2 min","Exception resolution"],["~0","Duplicate payments"],["$180K","Recovered in Q1"]] },
  { id: "o2c", n: "03", c: "Order to Cash", sub: "Cash application, collections, disputes, revenue validation",
    problem: "Payments arrive without remittance, dunning is one-size-fits-all, disputes sit unclassified and credit exposure lives in a stale spreadsheet.",
    how: "Agent matches payments to invoices using AI-read remittance, prioritizes collections by risk, sends smart dunning and validates revenue against contracts.",
    output: "Applied cash with match evidence, prioritized collections worklist, dispute dashboard and credit scorecards with early warnings.",
    workflows: ["Cash application","Collections prioritization","Customer aging analysis","Dispute & deduction tracking","Revenue validation","Contract-based billing checks","Credit risk monitoring","AR anomaly detection"],
    stats: [["11 days","DSO reduction"],["85%","Payments auto-matched"],["3×","Faster dispute resolution"],["−60%","Manual cash-app effort"]] },
  { id: "treasury", n: "04", c: "Treasury & FP&A", sub: "Cash management, forecasting, scenario planning, working capital",
    problem: "Rolling forecast takes a week and is stale by the time the board sees it. Cash positioning is manual and next-day at best.",
    how: "Agent rebuilds the forecast nightly against actuals, auto-detects variance drivers, consolidates cash positions in real time and models scenarios.",
    output: "Daily refreshed forecast, auto-written variance commentary, a scenario library with cash impact and a real-time cash dashboard.",
    workflows: ["Cash position tracking","Cash flow forecasting","Bank reconciliation","Liquidity monitoring","Budget vs actual analysis","Forecast variance analysis","Scenario planning","Working capital analysis"],
    stats: [["5→1","Analyst-days per cycle"],["Daily","Forecast freshness"],["12pts","MAPE improvement"],["Real-time","Cash visibility"]] },
  { id: "tax", n: "05", c: "Tax & Compliance", sub: "Tax code validation, regulatory reporting, compliance monitoring",
    problem: "Tax codes are assigned inconsistently, compliance checks are periodic not continuous, and regulatory changes surface after filing.",
    how: "Agent validates tax codes at the point of transaction, monitors compliance rules continuously and tracks regulatory changes to affected processes.",
    output: "Tax code validation report, VAT exception log, a continuous compliance dashboard and an audit-readiness scorecard.",
    workflows: ["Tax code validation","Sales tax / VAT checks","Compliance rule validation","Regulatory reporting support","Audit readiness checks","Transaction compliance monitoring","Exception identification"],
    stats: [["99.2%","Tax code accuracy"],["Continuous","Compliance, from quarterly"],["−65%","Audit prep time"],["0","Filing corrections"]] },
  { id: "reporting", n: "06", c: "Reporting & Analytics", sub: "Financial reports, KPI dashboards, ad-hoc queries",
    problem: "Reports are built manually in Excel, dashboards break when one person is out, and ad-hoc CFO questions take hours.",
    how: "Agent auto-generates period reports, maintains live KPI dashboards, answers ad-hoc questions in natural language and reconciles data across systems.",
    output: "Auto-generated financials with period comparisons, live KPI dashboards, a natural-language query interface and cross-system rec reports.",
    workflows: ["Financial report generation","KPI dashboards","Ad-hoc query (ask finance data)","Variance reporting","Trend analysis","Exception dashboards","Drill-down reporting","Cross-system reconciliation"],
    stats: [["−75%","Report prep time"],["<30s","Ad-hoc query response"],["Live","KPI dashboards"],["100%","Cross-system checks"]] },
];

export default function UseCasesPage() {
  const { openDemo } = useMarketing();
  const [open, setOpen] = React.useState("close");

  React.useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id && DOMAINS.some((d) => d.id === id)) {
      setOpen(id);
      setTimeout(() => {
        document.querySelector(`[data-testid="uc-panel-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  return (
    <>
      <EditorialHero
        eyebrow="Use cases · deep dives"
        title="AI agents that execute"
        italic="your finance workflows."
        sub="End-to-end execution with accuracy, control and trust — across every finance domain, on Oracle Fusion, Oracle EBS, Dynamics 365 and IFS."
      />

      <section className="py-20 sm:py-28 border-t bg-white"
               style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[260px_1fr] gap-12">
          {/* sticky side nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              {DOMAINS.map((d) => (
                <button key={d.id} onClick={() => setOpen(d.id)} data-testid={`uc-nav-${d.id}`}
                  className={`w-full text-left px-4 py-3 text-sm border-l-2 transition-all rounded-r-md ${
                    open === d.id
                      ? "border-[#C8A24B] text-[#C8A24B] bg-white font-semibold"
                      : "border-[#0B1E3F]/15 text-[#0B1E3F]/60 hover:text-[#0B1E3F] hover:border-[#0B1E3F]/35"
                  }`}>
                  <span className="font-mono text-xs mr-2 text-[#C8A24B]/80">{d.n}</span>{d.c}
                </button>
              ))}
            </div>
          </aside>

          {/* accordion */}
          <motion.div className="border-t" style={{ borderColor: "rgba(11,30,63,0.15)" }}
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            {DOMAINS.map((d) => {
              const isOpen = open === d.id;
              return (
                <motion.div key={d.id} variants={fadeUp} className="border-b" style={{ borderColor: "rgba(11,30,63,0.15)" }}>
                  <button onClick={() => setOpen(isOpen ? "" : d.id)} data-testid={`uc-toggle-${d.id}`}
                    className="w-full flex items-center justify-between gap-4 py-8 text-left group">
                    <div className="flex items-baseline gap-5">
                      <span className="font-serif text-2xl font-light" style={{ color: "rgba(200,162,75,0.85)" }}>{d.n}</span>
                      <span>
                        <span className="block font-serif text-2xl sm:text-3xl font-light tracking-tight group-hover:text-[#C8A24B] transition-colors"
                              style={{ color: "#0B1E3F" }}>{d.c}</span>
                        <span className="block text-sm mt-1 font-light" style={{ color: "rgba(11,30,63,0.6)" }}>{d.sub}</span>
                      </span>
                    </div>
                    <CaretDown size={20} weight="bold" className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "#C8A24B" }} />
                  </button>
                  {isOpen && (
                    <div className="pb-12 grid lg:grid-cols-2 gap-10" data-testid={`uc-panel-${d.id}`}>
                      <div className="space-y-6">
                        {[["Problem", d.problem], ["How it works", d.how], ["Output", d.output]].map(([h, t]) => (
                          <div key={h}>
                            <Overline>{h}</Overline>
                            <p className="text-sm mt-2 leading-relaxed font-light" style={{ color: "rgba(11,30,63,0.75)" }}>{t}</p>
                          </div>
                        ))}
                        <div>
                          <Overline>Workflows covered</Overline>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {d.workflows.map((w) => (
                              <span key={w} className="text-xs rounded-full px-3 py-1 font-mono bg-white"
                                    style={{ border: "1px solid rgba(11,30,63,0.15)", color: "rgba(11,30,63,0.75)" }}>{w}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 self-start">
                        {d.stats.map(([n, l]) => (
                          <div key={l} className="rounded-xl p-6 bg-white"
                               style={{ border: "1px solid rgba(11,30,63,0.12)" }}>
                            <div className="font-serif font-light text-4xl" style={{ color: "#C8A24B" }}>{n}</div>
                            <div className="text-xs mt-2 font-light inline-flex items-center gap-2" style={{ color: "rgba(11,30,63,0.7)" }}>
                              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#1F6B4A" }} />
                              {l}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-white py-24 sm:py-28 border-t" style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold" style={{ color: "#C8A24B" }}>A working session, not a slideshow</div>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02]" style={{ color: "#0B1E3F" }}>
              See it run in <span className="italic" style={{ color: "#C8A24B" }}>your environment</span>.
            </h2>
            <p className="mt-6 text-lg font-light max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(11,30,63,0.70)" }}>
              We plug into a sandbox of your ERP, run one workflow end-to-end on your data, and send you the output.
            </p>
            <div className="mt-8">
              <button onClick={openDemo} data-testid="usecases-cta-demo"
                className="group rounded-full px-8 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                style={{ background: "#0B1E3F", boxShadow: "0 12px 32px rgba(11,30,63,0.25)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#C8A24B")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#0B1E3F")}>
                <Check size={18} weight="bold" style={{ color: "#1F6B4A" }} /> Book a working session
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
